import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import { Search, SlidersHorizontal, X, RefreshCw } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function CataloguePage() {
  const { user } = useAuth();
  const { shippingAddress } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    minPrice: 0,
    maxPrice: 50000,
    category: searchParams.get("category") || "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [categories, setCategories] = useState([]);
  const location = useLocation();

  // Use a ref to access the latest filters inside fetchProducts without adding it to dependencies
  // This prevents the effect from running on minPrice/maxPrice changes (which update on drag)
  // while ensuring the fetch function always uses the latest values.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchProducts = useCallback(async () => {
    const currentFilters = filtersRef.current;

    setLoading(true);
    try {
      let url = `${API_URL}/products?`;
      const params = new URLSearchParams();

      if (currentFilters.search) params.append("search", currentFilters.search);

      // If searching by slug, use category_slug param
      // If searching by ID (legacy), use category_id
      if (currentFilters.category) {
        // Check if it looks like a UUID or a Slug
        if (currentFilters.category.length === 36 && currentFilters.category.includes('-')) {
          params.append("category_id", currentFilters.category);
        } else {
          params.append("category_slug", currentFilters.category);
        }
      }

      if (currentFilters.minPrice > 0) params.append("min_price", currentFilters.minPrice);
      if (currentFilters.maxPrice < 50000) params.append("max_price", currentFilters.maxPrice);

      const defaultAddr = shippingAddress || (user && user.saved_addresses ? (user.saved_addresses.find(a => a.id === user.default_address_id) || user.saved_addresses[0]) : null);
      if (defaultAddr && defaultAddr.coordinates) {
        const lat = defaultAddr.coordinates.lat;
        const lon = defaultAddr.coordinates.lon || defaultAddr.coordinates.lng;
        if (lat && lon) {
          params.append("user_lat", lat);
          params.append("user_lon", lon);
        }
      }

      const urlString = url + params.toString();
      const response = await axios.get(urlString);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/categories`);
      setCategories(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    // Sync state with URL params when URL changes
    setFilters(prev => ({
      ...prev,
      category: searchParams.get("category") || "",
      search: searchParams.get("search") || prev.search
    }));
  }, [searchParams]);

  useEffect(() => {
    fetchProducts();
  }, [filters.category, filters.search, fetchProducts]);

  // Re-fetch whenever the user navigates to this page (e.g. after editing a product)
  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducts();
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      minPrice: 0,
      maxPrice: 50000,
      category: "",
    });
    setSearchParams({}); // Clear URL
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Mobile Filter Toggle */}
          <div className="md:hidden mb-4">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setShowFilters(!showFilters)}
            >
              <SlidersHorizontal className="mr-2 h-4 w-4" />
              Filtres
            </Button>
          </div>

          {/* Sidebar Filters */}
          <div className={`w-full md:w-64 space-y-6 ${showFilters ? 'block' : 'hidden md:block'}`}>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg">Filtres</h3>
                {(filters.category || filters.search || filters.minPrice > 0) && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="h-auto p-0 text-xs text-red-500 hover:text-red-600">
                    Effacer
                  </Button>
                )}
              </div>

              {/* Categories */}
              <div className="space-y-3 mb-6">
                <h4 className="font-medium text-sm text-gray-700">Catégories</h4>
                <div className="space-y-2">
                  <div
                    className={`cursor-pointer text-sm ${!filters.category ? 'font-bold text-orange-500' : 'text-gray-600'}`}
                    onClick={() => {
                      setSearchParams({});
                    }}
                  >
                    Tout
                  </div>
                  {categories.map(cat => (
                    <div
                      key={cat.id}
                      className={`cursor-pointer text-sm ${filters.category === cat.slug ? 'font-bold text-orange-500' : 'text-gray-600'}`}
                      onClick={() => {
                        setSearchParams({ category: cat.slug });
                      }}
                    >
                      {cat.name}
                    </div>
                  ))}
                </div>
              </div>

              {/* Price */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-gray-700">Prix</h4>
                <Slider
                  defaultValue={[0, 50000]}
                  max={50000}
                  step={100}
                  onValueChange={(val) => setFilters(prev => ({ ...prev, minPrice: val[0], maxPrice: val[1] }))}
                  onValueCommit={fetchProducts}
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{filters.minPrice} HTG</span>
                  <span>{filters.maxPrice}+ HTG</span>
                </div>
              </div>
            </div>
          </div>

          {/* Product Grid */}
          <div className="flex-1">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-gray-900">
                {filters.category
                  ? categories.find(c => c.slug === filters.category)?.name || "Catalogue"
                  : "Tous les produits"}
              </h1>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">{products.length} résultats</span>
                <button
                  onClick={fetchProducts}
                  className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-orange-500 transition-colors"
                  title="Actualiser le catalogue"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-xl h-80 animate-pulse" />
                ))}
              </div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg">
                <p className="text-gray-500 text-lg">Aucun produit trouvé.</p>
                <Button variant="link" onClick={clearFilters} className="mt-2 text-orange-500">
                  Réinitialiser les filtres
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
